'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import {
  BodyDefault,
  Checkbox,
  Filter,
  Tooltip,
} from '@terra/ui-design-system';
import type { FindingHasPreventionFilter } from '../../../store/tab/findingsTabScopedFilterStore';
import {
  ListPageAdvancedFilterField,
  ListPageAdvancedFilters,
} from '../../common/ListPageAdvancedFilters';
import type { FindingsListFilters } from '../findings-table/useFindingsListFilters';
import {
  FINDINGS_PUBLICATION_CHIP,
  resolveFindingsPublicationChip,
} from '../findings-table/findings-list.model';

const HAS_PREVENTION_VALUES = ['yes', 'no'] as const satisfies readonly Exclude<
  FindingHasPreventionFilter,
  'ALL'
>[];

const toHasPreventionFilter = (
  value: string | undefined,
): FindingHasPreventionFilter =>
  HAS_PREVENTION_VALUES.find((option) => option === value) ?? 'ALL';

export function FindingsAdvancedFilters({
  filters,
}: {
  filters: FindingsListFilters;
}) {
  const tTable = useTranslations('designSystem.findingsList.table');
  const tFindingsFilters = useTranslations('designSystem.findingsList.filters');
  const tProxyFindings = useTranslations('proxy.findings.filters');
  const hasPreventionItems = useMemo(
    () =>
      HAS_PREVENTION_VALUES.map((value) => ({
        value,
        label: tFindingsFilters(`hasPreventionOptions.${value}`),
      })),
    [tFindingsFilters],
  );
  const isAutoPublishedUnavailable =
    resolveFindingsPublicationChip(filters.publishedFilter) ===
    FINDINGS_PUBLICATION_CHIP.UNPUBLISHED;

  return (
    <ListPageAdvancedFilters>
      <ListPageAdvancedFilterField>
        <Filter
          id="has-prevention"
          density="compact"
          placeholder={tFindingsFilters('hasPrevention')}
          items={hasPreventionItems}
          value={
            filters.hasPreventionFilter === 'ALL'
              ? []
              : [filters.hasPreventionFilter]
          }
          onChange={([value]) =>
            filters.setHasPreventionFilter(toHasPreventionFilter(value))
          }
        />
      </ListPageAdvancedFilterField>
      <ListPageAdvancedFilterField>
        <Tooltip
          title={tProxyFindings('autoPublishedUnavailableTooltip')}
          disabled={!isAutoPublishedUnavailable}
        >
          <Checkbox
            size="sm"
            checked={filters.publishedFilter === 'auto-published'}
            disabled={isAutoPublishedUnavailable}
            onCheckedChange={(details) =>
              filters.setPublishedFilter(
                details.checked === true ? 'auto-published' : 'published',
              )
            }
          >
            <BodyDefault color="fg.default" whiteSpace="nowrap">
              {tTable('autoPublished')}
            </BodyDefault>
          </Checkbox>
        </Tooltip>
      </ListPageAdvancedFilterField>
    </ListPageAdvancedFilters>
  );
}
